// src/index.ts
import { z } from "zod";
var PROTOCOL_VERSION = "machine-wilds/1";
var jsonValueSchema = z.lazy(
  () => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema)
  ])
);
var presentationSchema = z.object({
  name: z.string().trim().min(1).max(80),
  bio: z.string().max(1e3).optional(),
  symbol: z.string().refine((value) => Array.from(value).length <= 8, "Symbol must be at most 8 code points").optional(),
  capabilities: z.array(z.string().trim().min(1).max(64)).max(20).default([]),
  metadata: z.record(z.string(), jsonValueSchema).default({})
}).strict().superRefine((value, context) => {
  if (new TextEncoder().encode(JSON.stringify(value.metadata)).byteLength > 4096) {
    context.addIssue({ code: "custom", path: ["metadata"], message: "Metadata exceeds 4 KiB" });
  }
  if (new TextEncoder().encode(JSON.stringify(value)).byteLength > 8192) {
    context.addIssue({ code: "custom", message: "Presentation exceeds 8 KiB" });
  }
});
var publicKeySchema = z.string().regex(/^ed25519:[A-Za-z0-9_-]{43}$/, "Expected ed25519:<base64url raw 32-byte key>");
var signatureSchema = z.string().regex(/^[A-Za-z0-9_-]{86}$/, "Expected unpadded base64url Ed25519 signature");
var messageBodySchema = z.string().trim().min(1).max(2e3);
var placeSlugSchema = z.string().min(1).max(64).regex(/^[a-z0-9-]+$/);
var enterRequestSchema = z.object({ presentation: presentationSchema, publicKey: publicKeySchema.optional() }).strict();
var verifyRequestSchema = z.object({
  challengeId: z.string().uuid(),
  signature: signatureSchema,
  presentation: presentationSchema
}).strict();
var sayRequestSchema = z.object({ body: messageBodySchema }).strict();
var directMessageRequestSchema = sayRequestSchema;
var moveRequestSchema = z.object({}).strict();
var ProtocolError = class extends Error {
  constructor(code, message, statusCode = 400, details) {
    super(message);
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    this.name = "ProtocolError";
  }
  code;
  statusCode;
  details;
};
var wsEnvelopeSchema = z.object({
  protocol: z.literal(PROTOCOL_VERSION),
  requestId: z.string().min(1).max(100),
  operation: z.enum([
    "session.authenticate",
    "observer.subscribe",
    "space.look",
    "space.say",
    "agent.message",
    "agent.inspect",
    "place.move"
  ]),
  data: z.record(z.string(), z.unknown()).default({})
}).strict();
function wsResult(requestId, data) {
  return { protocol: PROTOCOL_VERSION, requestId, type: "result", data };
}
function wsError(requestId, code, message) {
  return {
    protocol: PROTOCOL_VERSION,
    requestId,
    type: "error",
    error: { code, message }
  };
}
export {
  PROTOCOL_VERSION,
  ProtocolError,
  directMessageRequestSchema,
  enterRequestSchema,
  messageBodySchema,
  moveRequestSchema,
  placeSlugSchema,
  presentationSchema,
  publicKeySchema,
  sayRequestSchema,
  signatureSchema,
  verifyRequestSchema,
  wsEnvelopeSchema,
  wsError,
  wsResult
};
